import { PreviousUpload, Upload, UploadOptions } from "@maysoft/tus-js-client";
import { useCallback, useEffect, useRef, useState } from "react";

import nanoid from "../../lib/nanoid";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import ResumableUploadService, { ResumableUploadRequest } from "../../services/asset/resumableUpload.service";
import { Method } from "../../services/base/HttpClient";
import { useCommonComponentContext } from "../../providers/CommonComponentProvider";
import { UploadFileResponse } from "../../commons/interfaces";
import { FileAccessMode, FileProcessingStatus } from "../../constants/enums";

interface IProps {
    fileId?: string;
    optimize?: boolean;
    errorMessage?: string;
    folderPath?: string;
    accessMode?: FileAccessMode;
    onChangeImage: (res?: UploadFileResponse) => void;
}

interface ExtendedPreviousUpload extends PreviousUpload {
    uploadUrl: string;
    urlStorageKey: string;
}

const useImageUploader = ({ fileId, optimize, errorMessage, folderPath, accessMode = FileAccessMode.Private, onChangeImage }: IProps) => {
    const Strings = useStrings();
    const { tenantCode, serviceCode } = useCommonComponentContext();

    const imageInputId = nanoid();
    const imageInputRef = useRef<any>(null);
    const tusUpload = useRef<Upload>();
    const tusUploadUrl = useRef<string>();

    const [originalFileId, setOriginalFileId] = useState<string>();
    const [uploading, setUploading] = useState(false);
    const [pausing, setPausing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(errorMessage || "");
    
    const uploadProgressRef = useRef(uploadProgress);
    
    useEffect(() => {
        if (!Helpers.isNullOrEmpty(fileId) && fileId !== originalFileId) {
            setOriginalFileId(fileId);
        }
    }, [fileId]);

    const uploadFile = async (file: File) => {
        setError("");
        setUploadProgress(0);

        if (file.name.includes(".exe")) {
            setError(Strings.Common.INVALID_FILE);
            return;
        }

        try {
            const request: ResumableUploadRequest = {
                accessMode: `${accessMode}`,
                tenantCode: tenantCode,
                serviceCode: serviceCode,
                processingStatus: (optimize === false)
                    ? `${FileProcessingStatus.Processed}`
                    : `${FileProcessingStatus.Unprocessed}`,
            };

            if (!Helpers.isNullOrEmpty(folderPath)) {
                request.folderPath = folderPath;
            }

            const options: UploadOptions = {
                onBeforeRequest: (req) => {
                    if (Helpers.isNullOrEmpty(tusUploadUrl.current) && req.getMethod() === Method.PATCH) {
                        tusUploadUrl.current = req.getURL();
                    }
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = parseInt(((bytesUploaded / bytesTotal) * 100).toFixed(2));
                    setUploadProgress(percentage);
                    uploadProgressRef.current = percentage;
                },
                onSuccess: (res) => {
                    let id = "";
                    if (res.getHeader("asset-id")) {
                        id = res.getHeader("asset-id");
                    }

                    let accessUrl = "";
                    if (res.getHeader("asset-url")) {
                        accessUrl = res.getHeader("asset-url");
                    }

                    let error = "";
                    if (res.getHeader("asset-error")) {
                        error = res.getHeader("asset-error");
                        setError(error);
                        handleUploadError();
                    }

                    const response = {
                        id,
                        error,
                        accessUrl,
                    };

                    tusUpload.current = undefined;
                    tusUploadUrl.current = undefined;
                    onChangeImage && onChangeImage(response);
                    setUploading(false);
                },
                onError: (error) => {
                    handleUploadError();
                },
            };

            tusUpload.current = ResumableUploadService.upload(file, request, options);

            setUploading(true);
            tusUpload.current.start();
        } catch (error) {
            if (tusUpload.current) {
                tusUpload.current.abort();
            }
            handleUploadError();
        }
    };

    const startOrResumeUpload = (tusUpload: Upload) => {
        tusUpload
            .findPreviousUploads()
            .then((allPreviousUploads) => {
                for (let index = 0; index < allPreviousUploads.length; index++) {
                    const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                    if (previousUpload.uploadUrl === tusUploadUrl.current) {
                        tusUpload.resumeFromPreviousUpload(allPreviousUploads[index]);
                        break;
                    }
                }
                tusUpload.start();
            })
            .catch((error) => {
                console.log("Error when start/resume upload:", error);
            });
    };

    const terminateUpload = () => {
        // Abort upload first, then remove upload key in localStorage
        if (tusUpload.current) {
            tusUpload.current.abort();
            tusUpload.current
                .findPreviousUploads()
                .then((allPreviousUploads) => {
                    for (let index = 0; index < allPreviousUploads.length; index++) {
                        const previousUpload = allPreviousUploads[index] as ExtendedPreviousUpload;
                        if (previousUpload.uploadUrl === tusUploadUrl.current) {
                            localStorage.removeItem(previousUpload.urlStorageKey);
                            break;
                        }
                    }
                    tusUploadUrl.current = undefined;
                })
                .catch((error) => {
                    console.log("Error when abort upload:", error);
                });
        }

        // Make http request to terminate upload
        if (tusUploadUrl.current) {
            Upload.terminate(tusUploadUrl.current).catch((error) => {
                console.log("Error when terminate upload:", error);
            });
        }
    };

    const handleContinueOrPauseUpload = useCallback(() => {
        if (tusUpload.current && uploadProgress < 100) {
            if (pausing) {
                setPausing(false);
                startOrResumeUpload(tusUpload.current);
            } else {
                setPausing(true);
                tusUpload.current.abort();
            }
        }
    }, [pausing, uploadProgress]);

    const handleTerminateUpload = useCallback(() => {
        if (uploadProgress < 100) {
            setPausing(true);
            tusUpload.current && tusUpload.current.abort();
            Helpers.showConfirmAlert(
                Strings.Common.CANCEL_UPLOAD_IMAGE,
                () => {
                    terminateUpload();
                    imageInputRef.current.value = null;
                    onChangeImage(undefined);
                    setOriginalFileId(undefined);
                    setUploading(false);
                },
                () => {
                    setPausing(false);
                    tusUpload.current && startOrResumeUpload(tusUpload.current);
                },
                Strings.Common.YES,
                Strings.Common.NO
            );
        }
    }, [uploadProgress]);

    const handleUploadError = () => {
        if (imageInputRef.current) {
            imageInputRef.current.value = null;
            setUploading(false);
            setUploadProgress(0);
            setOriginalFileId(undefined);
            setError(Strings.Common.UPLOAD_IMAGE_FAILED);
        }
        onChangeImage(undefined);
    };

    const handleRemove = () => {
        imageInputRef.current.value = null;
        setOriginalFileId(undefined);
        onChangeImage(undefined);
    };

    return {
        error,
        uploadProgress,
        originalFileId,
        uploading,
        pausing,
        imageInputId,
        imageInputRef,
        handleRemove,
        handleTerminateUpload,
        handleContinueOrPauseUpload,
        uploadFile,
    };
};

export default useImageUploader;
